import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AudioFormat } from '../types';
import { getOrRefreshUrl } from '../api/audioStreamUrl';

export interface PlayerTrack {
  id: number | string;
  title: string;
  documentId?: number;
  format: AudioFormat | 'tts';
  durationSeconds?: number;
  // When set, played directly (e.g. a local blob URL for direct-read). Otherwise
  // the signed URL is resolved from documentId.
  directUrl?: string;
}

interface PlayerState {
  current: PlayerTrack | null;
  isPlaying: boolean;
  isLoading: boolean;
  position: number;
  duration: number;
  playbackRate: number;
  play: (track: PlayerTrack) => Promise<void>;
  toggle: () => void;
  seekTo: (seconds: number) => void;
  seekBy: (delta: number) => void;
  setPlaybackRate: (rate: number) => void;
  stop: () => void;
}

const AudioPlayerContext = createContext<PlayerState | undefined>(undefined);

export const SPEED_OPTIONS = [0.75, 1.0, 1.25, 1.5, 1.75, 2.0];

export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [current, setCurrent] = useState<PlayerTrack | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setRate] = useState(1);

  if (!audioRef.current && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.preload = 'metadata';
  }

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setPosition(el.currentTime);
    const onDur = () => setDuration(el.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onPlaying = () => setIsLoading(false);
    const onError = async () => {
      // Signed URL may have expired; refresh once and resume.
      if (current?.documentId && !current.directUrl) {
        try {
          const url = await getOrRefreshUrl(current.documentId, true);
          const t = el.currentTime;
          el.src = url;
          el.load();
          el.currentTime = t;
          await el.play();
        } catch {
          setIsLoading(false);
        }
      }
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('durationchange', onDur);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('error', onError);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('durationchange', onDur);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('error', onError);
    };
  }, [current]);

  const play = useCallback(async (track: PlayerTrack) => {
    const el = audioRef.current;
    if (!el) return;
    setCurrent(track);
    setIsLoading(true);
    try {
      let url = track.directUrl;
      if (!url && track.documentId) url = await getOrRefreshUrl(track.documentId);
      if (!url) throw new Error('No playable URL');
      el.src = url;
      el.load();
      el.playbackRate = playbackRate;
      await el.play();
    } catch {
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  }, [playbackRate]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    if (el.paused) el.play();
    else el.pause();
  }, [current]);

  const seekTo = useCallback((seconds: number) => {
    const el = audioRef.current;
    if (el) el.currentTime = Math.max(0, Math.min(seconds, el.duration || seconds));
  }, []);

  const seekBy = useCallback((delta: number) => {
    const el = audioRef.current;
    if (el) el.currentTime = Math.max(0, el.currentTime + delta);
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    setRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, []);

  const stop = useCallback(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    setCurrent(null);
    setIsPlaying(false);
    setPosition(0);
    setDuration(0);
  }, []);

  const value = useMemo<PlayerState>(
    () => ({ current, isPlaying, isLoading, position, duration, playbackRate, play, toggle, seekTo, seekBy, setPlaybackRate, stop }),
    [current, isPlaying, isLoading, position, duration, playbackRate, play, toggle, seekTo, seekBy, setPlaybackRate, stop],
  );

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
}

export function useAudioPlayer(): PlayerState {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  return ctx;
}
